type LogType = 'info' | 'warn' | 'error' | 'ai';

interface LogEntry {
  timestamp: string;
  message: string;
  type: LogType;
  id: string;
  metadata?: unknown;
}

const STORAGE_KEY = 'techtaitan_system_logs';
const MAX_LOGS = 100;

class SystemLogger {
  private static instance: SystemLogger;
  private logs: LogEntry[] = [];
  private listeners: ((logs: LogEntry[]) => void)[] = [];

  private constructor() {
    this.loadFromStorage();
  }

  public static getInstance(): SystemLogger {
    if (!SystemLogger.instance) {
      SystemLogger.instance = new SystemLogger();
    }
    return SystemLogger.instance;
  }

  private loadFromStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          this.logs = JSON.parse(stored);
        }
      }
    } catch (error) {
      console.warn(
        'Failed to load logs from storage. This is expected in some sandboxed environments.',
        error
      );
      this.logs = [];
    }
  }

  private saveToStorage() {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logs));
      }
    } catch (error) {
      console.warn(
        'Failed to save logs to storage. This may occur in sandboxed environments.',
        error
      );
    }
  }

  public log(message: string, type: LogType = 'info', metadata?: unknown) {
    const entry: LogEntry = {
      timestamp: new Date().toLocaleTimeString(),
      message,
      type,
      id: Math.random().toString(36).substr(2, 9),
      metadata,
    };

    this.logs = [entry, ...this.logs].slice(0, MAX_LOGS);

    // Console output for dev convenience
    let metaStr = '';
    if (metadata) {
      try {
        metaStr = ` | Meta: ${JSON.stringify(metadata).substring(0, 100)}...`;
      } catch {
        metaStr = ' | Meta: [unserializable]';
      }
    }
    console.log(`[${entry.type.toUpperCase()}] ${entry.timestamp}: ${entry.message}${metaStr}`);

    this.saveToStorage();
    this.notify();
  }

  public clear() {
    this.logs = [];
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.warn(
        'Failed to clear logs from storage. This may occur in sandboxed environments.',
        error
      );
    }
    this.notify();
  }

  public subscribe(listener: (logs: LogEntry[]) => void) {
    this.listeners.push(listener);
    listener(this.logs);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  private notify() {
    this.listeners.forEach((l) => l(this.logs));
  }

  public getLogs() {
    return this.logs;
  }
}

export const logger = SystemLogger.getInstance();
export type { LogEntry, LogType };
